import { html } from "lit-element";
import { StatefulModule } from "../ModuleBase/ModuleMetadata";
import { BooleanField } from "../../../elements/inspx-boolean-field";

export class DrawShapeBaseModule extends StatefulModule {
	Activate() {
		this.canvas.defaultCursor = "crosshair";
		this.canvas.on("mouse:down", this.draw);
		return this;
	}

	Deactivate() {
		this.canvas.setCursor(this.canvas.defaultCursor);
		this.canvas.off("mouse:down", this.draw);
	}

	render() {
		return html`
        <style>
        :host {
            padding: 5px;
            cursor: pointer;
        }

        svg {
            height: 100%;
            width: 100%;
            cursor: pointer;
        }
        </style>
        <label>${this.icon}</label>`;
	}

	constructor(canvas, getColor) {
		super();
		this.canvas = canvas;
		this.__getColor = getColor;
		this.setAttribute("shape-base", "shape-base");

		this.draw = o => {
			const pointer = this.canvas.getPointer(o.e);
			Promise.resolve(this._getShape(pointer))
				.then(obj => {
					if (obj) {
						obj.selectable = false;
						this.canvas.add(obj);
						this.addMetadata(obj, { stroke: obj.stroke, strokeWidth: obj.strokeWidth, fill: obj.fill, borderStyle: "solid" });
						this.canvas.renderAll();
					}
				})
				.then(() => {
					this.fireOnDraw();
				});
		};
	}

	RenderSettings(obj, closeFunc) {
		let currentSettings = obj.moduleState;
		let tempWidth = currentSettings.strokeWidth;
		let tempBorderColor = currentSettings.stroke;
		let tempFillColor = currentSettings.fill;
		let tempBorderStyle = currentSettings.borderStyle || "solid";

		let cbSolid = new BooleanField();
		cbSolid.label = "solid";
		cbSolid.type = "checkbox";
		cbSolid.value = tempBorderStyle == "solid";

		let cbDotted = new BooleanField();
		cbDotted.label = "dotted";
		cbDotted.type = "checkbox";
		cbDotted.value = tempBorderStyle == "dotted";

		let cbDashed = new BooleanField();
		cbDashed.label = "dashed";
		cbDashed.type = "checkbox";
		cbDashed.value = tempBorderStyle == "dashed";

		cbDashed.addEventListener('change', (e) => {
			cbSolid.value = false; cbDotted.value = false;
			if (e.detail.value) tempBorderStyle = "dashed";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});
		cbSolid.addEventListener('change', (e) => {
			cbDotted.value = false; cbDashed.value = false;
			if (e.detail.value) tempBorderStyle = "solid";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});
		cbDotted.addEventListener('change', (e) => {
			cbDashed.value = false; cbSolid.value = false;
			if (e.detail.value) tempBorderStyle = "dotted";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});

		// border color
		let tempBorderTransp = (tempBorderColor === this.transpColor);

		let cbBorderColor = new BooleanField();
		cbBorderColor.label = "transparent ";
		cbBorderColor.type = "checkbox";
		cbBorderColor.value = tempBorderTransp;
		cbBorderColor.addEventListener('change', (e) => {
			tempBorderTransp = e.detail.value;
			if (tempBorderTransp) {
				inpBorderColor.setAttribute("disabled", "");
				cbFillColor.setAttribute("disabled", "");
			} else {
				inpBorderColor.removeAttribute("disabled");
				cbFillColor.removeAttribute("disabled");
			}
			inpBorderColor.value = tempBorderColor = this.inputTranspColor;
		});

		let inpBorderColor = document.createElement("input");
		inpBorderColor.type = "color";
		inpBorderColor.id = "bColor";
		inpBorderColor.value = tempBorderTransp ? this.inputTranspColor : tempBorderColor;
		inpBorderColor.addEventListener('change', (e) => {
			tempBorderColor = e.currentTarget.value;
		});

		// fill color
		let tempFillTransp = (tempFillColor === this.transpColor);

		let cbFillColor = new BooleanField();
		cbFillColor.label = "transparent ";
		cbFillColor.type = "checkbox";
		cbFillColor.value = tempFillTransp;
		cbFillColor.addEventListener('change', (e) => {
			tempFillTransp = e.detail.value;
			if (tempFillTransp) {
				inpFillColor.setAttribute("disabled", "");
				cbBorderColor.setAttribute("disabled", "");
			} else {
				inpFillColor.removeAttribute("disabled");
				cbBorderColor.removeAttribute("disabled");
			}
			inpFillColor.value = tempFillColor = this.inputTranspColor;
		});

		let inpFillColor = document.createElement("input");
		inpFillColor.type = "color";
		inpFillColor.id = "fColor";
		inpFillColor.value = tempFillTransp ? this.inputTranspColor : tempFillColor;
		inpFillColor.addEventListener('change', (e) => {
			tempFillColor = e.currentTarget.value;
		});

		if (tempBorderTransp) {
			inpBorderColor.setAttribute("disabled", "");
			cbFillColor.setAttribute("disabled", "");
		}
		if (tempFillTransp) {
			inpFillColor.setAttribute("disabled", "");
			cbBorderColor.setAttribute("disabled", "");
		}

		const applyFunc = () => {
			let border = (tempBorderTransp ? this.transpColor : tempBorderColor);
			let fill = (tempFillTransp ? this.transpColor : tempFillColor);
			this.applyNewSettings(obj, tempWidth, border, fill, tempBorderStyle);
			closeFunc();
		};

		return html`
            <style>
                .title {
                    font-size: x-large;    
                    font-weight: bold;
                    margin-bottom: 20px;
                }

                .button-box {
                    display: flex;
                    margin-top: 20px;
                }

                .cPick {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    color: var(--secondary-color-variant1);
                }

                .color-box {
                    color: var(--secondary-color-variant1);
                }
            
                .color-box boolean-field {
                    width: auto;
                }

                input:disabled,
                boolean-field[disabled] {
                    color: rgba(84, 84, 84, .5);
                    cursor: not-allowed;
                    opacity: .5;
                }
            </style>

            <div class="title">${this._getSettingsHeader()}</div>
            <div>
                <text-box type="text" label="Border width" 
                    @change="${e => (tempWidth = e.currentTarget.value)}"
                    .value="${tempWidth}"></text-box>

                    <div class="color-box">
					<label>Border style</label> 
					<div class="cPick">
						${cbSolid}
						${cbDotted}
						${cbDashed}
					</div>
				</div>
                <br/>

                <div class="color-box">
					<label for="bColor">Border color</label> 
					<div class="cPick"> 
                        ${cbBorderColor}
                        ${inpBorderColor}
					</div>
				</div>
				<br/>

				<div class="color-box">
					<label for="fColor">Fill color</label> 
					<div class="cPick"> 
                        ${cbFillColor}
                        ${inpFillColor}
					</div>
				</div>
			</div>

			<div class="button-box">
				<mwc-button @click="${closeFunc}" label="Close"></mwc-button>
				<mwc-button @click="${applyFunc}" label="Apply"></mwc-button>
			</div>`;
	}

	applyNewSettings(obj, strokeWidth, stroke, fill, borderStyle) {
		const currentSettings = obj.moduleState;
		if (currentSettings.strokeWidth == strokeWidth
            && currentSettings.stroke == stroke
            && currentSettings.fill == fill
            && currentSettings.borderStyle == borderStyle) return;

		let strokeDashArray = undefined;
		if (borderStyle == 'dotted') strokeDashArray = [strokeWidth, strokeWidth];
		if (borderStyle == 'dashed') strokeDashArray = [4 * strokeWidth, strokeWidth];

		this.addMetadata(obj, { strokeWidth: Number(strokeWidth), stroke, fill, borderStyle });
		obj.set({ strokeWidth: Number(strokeWidth), stroke, fill, strokeDashArray });

		this.canvas.renderAll();
		this.canvas.trigger("object:modified", { target: obj });
	}
}
